import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef, } from '@angular/material/dialog';
import { UploadAssetsEventsComponent } from '../../components/upload-assets-events/upload-assets-events.component';
import { VersionService } from '../../services/version.service';
import { Event, EventService, OfferPromo, OfferPromoService, PromoDomain, Version, } from '../..';
import { DEFAULT_SNACKBAR_CONFIG, Logger } from 'src/app/@shared';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CloneType } from '../../models/clone-type.model';
import { IPrintPage } from '../../models/print-page.model';
import { finalize } from 'rxjs/operators';

interface DialogData {
  eventId: string;
}

const log = new Logger('CloneOffersComponent');

@Component({
  selector: 'app-clone-offers',
  templateUrl: './clone-offers.component.html',
  styleUrls: ['./clone-offers.component.scss'],
})
export class CloneOffersComponent<
  TVersion extends Version,
  TOfferPromo extends OfferPromo,
  TPromoDomain extends PromoDomain<OfferPromo>,
  TEvent extends Event
> implements OnInit {
  cloneForm: FormGroup = new FormGroup({
    sourceEvent: new FormControl(null, [Validators.required]),
    destinationEvent: new FormControl(null, [Validators.required]),
    versions: new FormControl([]),
    IncludeVersions: new FormControl(true),
    KeepCurrentStatus: new FormControl(true),
    PrintPageId: new FormControl(null),
  });
  versionList: TVersion[] = [];
  @ViewChild('searchInput') searchInput: any;
  sourceEventId: string = '';
  destinationEventId: string = '';
  viewMode = 'TABLE';
  @ViewChild('offerPromoCloneTable', { static: false })
  offerPromoCloneTable: any;
  @ViewChild('offerPromoCloneCards', { static: false })
  offerPromoCloneCards: any;
  entityType$ = this.offerpromoService.cloneEntityType$;
  CloneType = CloneType;
  printPagesList: IPrintPage[] = [];
  isCloning = false;

  constructor(
    private dialog: MatDialog,
    private versionService: VersionService<TVersion>,
    private offerpromoService: OfferPromoService<TOfferPromo, TPromoDomain>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private eventService: EventService<TEvent>,
    public dialogRef: MatDialogRef<
      CloneOffersComponent<TVersion, TOfferPromo, TPromoDomain, TEvent>
    >,
    private matSnackBar: MatSnackBar,
    private offerPromoService: OfferPromoService<TOfferPromo, TPromoDomain>
  ) {
    this.sourceEventId = this.data.eventId;
    this.destinationEventId = this.data.eventId;
    this.offerpromoService.cloneEventId = this.data.eventId;
    this.eventService.getEvent(this.sourceEventId).subscribe((res) => {
      this.cloneForm.controls['sourceEvent'].setValue(res.EventName);
      this.cloneForm.controls['destinationEvent'].setValue(res.EventName);
      this.getVersionsList(this.sourceEventId);
    });
    this.getPrintPageDetailsByEvent();
    this.cloneForm.controls['versions'].valueChanges.subscribe(res => {
      if (res?.length >= this.versionList.length && !res?.includes('allVersions')) {
        this.cloneForm.controls['versions'].setValue([...this.cloneForm.controls['versions'].value, 'allVersions']);
      } else if (res?.length <= this.versionList.length && res?.includes('allVersions')) {
        const values = [...this.cloneForm.controls['versions'].value.filter((val: any) => val !== 'allVersions')];
        this.cloneForm.controls['versions'].setValue(values);
      }
    })
  }
  ngOnInit(): void {
    log.debug('init');
  }

  getPrintPageDetailsByEvent() {
    this.offerPromoService.GetAllPrintPagesByEventforOffers(this.sourceEventId).subscribe((res) => {
      this.printPagesList = res;
    })
  }

  onFilterChange(event: any) {
    this.offerPromoService.cloneOffersPrintPages = event.value;
  }

  get selectedRecords() {
    if (this.offerPromoCloneCards) {
      return this.offerPromoCloneCards.getSelectedSectionRecords();
    }
    if (this.offerPromoCloneTable) {
      return this.offerPromoCloneTable.getSelectedSectionRecords();
    }
    return [];
  }

  openSourceEventsDialog() {
    const sourceEventsDialog = this.dialog.open(UploadAssetsEventsComponent, {
      width: '60%',
      height: '90%',
      data: { includeExpiredEvents: true },
    });
    sourceEventsDialog.afterClosed().subscribe((res) => {
      if (res) {
        this.cloneForm.controls['sourceEvent'].patchValue(
          res.Detail?.EventName
        );
        this.sourceEventId = res.Detail?.Id;
        this.offerpromoService.cloneEventId = res.Detail?.Id;
        this.getPrintPageDetailsByEvent();
      }
    });
  }

  openDestinationEventsDialog() {
    const destinationEventsDialog = this.dialog.open(UploadAssetsEventsComponent, {
      width: '60%',
      height: '90%',
      data: { includeExpiredEvents: true },
    });
    destinationEventsDialog.afterClosed().subscribe((res) => {
      if (res) {
        this.cloneForm.controls['destinationEvent'].patchValue(
          res.Detail?.EventName
        );
        this.destinationEventId = res.Detail?.Id;
        this.getVersionsList(res.Detail?.Id);
      }
    });
  }

  getVersionsList(eventId: string) {
    this.versionService.getVersionsForVariants(eventId).subscribe((res) => {
      this.versionList = res;
    });
  }

  onSearch(event: any) {
    this.offerpromoService.cloneSearch(event.target.value);
  }

  clearSearch() {
    this.offerpromoService.cloneSearch('');
    this.searchInput && (this.searchInput.nativeElement.value = '');
  }

  cloneEvent() {
    if (this.isCloning) {
      return;
    }
    this.isCloning = true;

    const versionIds = [...this.cloneForm.controls['versions'].value.filter((version: string) => version !== 'allVersions')];
    const request: any = {
      SourceEventId: this.sourceEventId,
      DestinationEventId: this.destinationEventId,
      OfferPromoIds: this.selectedRecords.map((record: any) => record.Detail.Id),
      VersionIds: versionIds,
      IncludeVersions: this.cloneForm.controls['IncludeVersions'].value,
      KeepCurrentStatus: this.cloneForm.controls['KeepCurrentStatus'].value,
      OfferPromoTargetPrintPageIds: Object.fromEntries(this.offerPromoCloneTable.offerToPrintPageMap)
    };

    if (this.sourceEventId != this.destinationEventId) {
      request.IncludeVersions = false;
    }

    this.offerpromoService.cloneOfferPromos(request)
      .pipe(finalize(() => { this.isCloning = false; }))
      .subscribe({
        next: (res: any) => {
          this.matSnackBar.open(
            `Offer/Promo(s) cloned`,
            'OK',
            DEFAULT_SNACKBAR_CONFIG
          );
          this.dialogRef.close();
          this.offerpromoService.reload();
          this.offerpromoService.cloneEntity(CloneType.EVENT);
        },
        error: (error) => {
          if (error.status === 500) {
            log.error('500 Error cloning offers/promos', error);
            this.matSnackBar.open('500 Error cloning offers/promos', 'Error', { verticalPosition: 'top', panelClass: ['snackbar-error'] });
          }
          else if (error.status === 400) {
            const apiValidations = error.error;

            if (Array.isArray(apiValidations) && typeof apiValidations[0] === 'string') {
              this.matSnackBar.open(apiValidations[0], 'Error', { verticalPosition: 'top', panelClass: ['snackbar-error'] });
              return;
            }

            if (Array.isArray(apiValidations)) {
              const errorMessages: string[] = [];
              apiValidations.forEach((validation: any) => {
                if (!validation?.PropertyName) {
                  errorMessages.push(validation?.ErrorMessage);
                  return;
                }
                const controlPath = validation.PropertyName;
                const control = this.cloneForm?.get(controlPath);
                if (control) {
                  control.markAsTouched();
                  control.setErrors({
                    ...(control.errors || {}),
                    apiError: validation.ErrorMessage
                  });
                } else {
                  errorMessages.push(validation.ErrorMessage);
                }
              });

              if (errorMessages.length > 0) {
                this.matSnackBar.open(errorMessages[0], 'Error', { verticalPosition: 'top', panelClass: ['snackbar-error'] });
              }
              this.cloneForm.updateValueAndValidity();
            }
            else if (apiValidations?.error?.message) {
              this.matSnackBar.open(apiValidations.error.message, 'Error', { verticalPosition: 'top', panelClass: ['snackbar-error'] });
            }
            else if (apiValidations?.message) {
              this.matSnackBar.open(apiValidations.message, 'Error', { verticalPosition: 'top', panelClass: ['snackbar-error'] });
            }
            else if (typeof apiValidations === 'string') {
              this.matSnackBar.open(apiValidations, 'Error', { verticalPosition: 'top', panelClass: ['snackbar-error'] });
            }
            else {
              this.matSnackBar.open(typeof apiValidations === 'object' && apiValidations?.Message ? apiValidations.Message : apiValidations, 'Error', { verticalPosition: 'top', panelClass: ['snackbar-error'] });
            }
          }
          else if (error.status === 429) {
            this.matSnackBar.open(`Too many requests. Please wait a moment and retry. (statusCode; ${error.status})`, 'Error', { verticalPosition: 'top', panelClass: ['snackbar-error'] });
          }
          else if (error.status === 504 || error.status === 408) {
            this.matSnackBar.open(`Server is taking too long to respond. Please try again. (statusCode; ${error.status})`, 'Error', { verticalPosition: 'top', panelClass: ['snackbar-error'] });
          }
          else if (error.status === 0) {
            this.matSnackBar.open(`Request timed out or network issue. Check your connection. (statusCode; ${error.status})`, 'Error', { verticalPosition: 'top', panelClass: ['snackbar-error'] });
          }
          else {
            const message = error?.error?.message || error?.message || `Unexpected error. (statusCode; ${error.status})`;
            this.matSnackBar.open(message, 'Error', { verticalPosition: 'top', panelClass: ['snackbar-error'] });
          }
        }
      });
  }

  closeDialog() {
    this.dialogRef.close();
    this.offerpromoService.cloneEntity(CloneType.EVENT);
  }

  toggleSelectAllVersions(event: any) {
    if (this.cloneForm.controls['versions'].value.length >= this.versionList.length) {
      this.cloneForm.controls['versions'].setValue(null);
    } else {
      this.cloneForm.controls['versions'].setValue([...this.versionList.map(version => version.Id), 'allVersions']);
    }
  }
}
